import {IconButton} from '@wandb/components';
// @wandb/components Tooltip portals behind the semantic-ui Modal; swap back when Modal migrates off semantic-ui.
// oxlint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useCallback} from 'react';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {Analytics} from '../../../services/analytics';
import type {Config} from '../../../util/runfeed';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';

type LockColumnsToggleProps = {
  config: Config;
  onUpdate: (config: Config) => void;
};

export const LockColumnsToggle = ({
  config,
  onUpdate,
}: LockColumnsToggleProps) => {
  const isInReport = useIsInLoadedReportContext();
  const analyticsLocation = useRunSelectorAnalyticsLocation();

  const locked = config.lockColumns ?? false;

  const onPress = useCallback(() => {
    if (locked) {
      // unlock columns. existing hidden columns stay hidden until manually
      // added; future discovered columns auto-appear.
      onUpdate({...config, lockColumns: false});
    } else {
      // lock columns. freeze the currently-visible (incl. auto-discovered)
      // columns into the saved allowlist; future discovered columns are
      // added hidden.
      onUpdate({
        ...config,
        columnVisible: {...config.columnVisible},
        columnPinned: {...config.columnPinned},
        columnWidths: {...config.columnWidths},
        columnOrder: [...config.columnOrder],
        lockColumns: true,
      });
    }
    Analytics.track('Lock columns toggled', {
      location: analyticsLocation,
      locked: !locked,
    });
  }, [config, locked, onUpdate, analyticsLocation]);

  if (!isInReport) {
    return null;
  }

  const tooltipText = locked
    ? 'Unlock to add new columns automatically'
    : 'Lock to keep new columns hidden';

  return (
    <Tooltip
      position="top right"
      trigger={
        <IconButton
          aria-label={tooltipText}
          icon={locked ? 'lock-closed' : 'lock-open'}
          variant="ghost"
          onPress={onPress}
        />
      }>
      {tooltipText}
    </Tooltip>
  );
};
